import React, { useCallback, useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  XIcon,
  SaveIcon,
  PlusIcon,
  Trash2Icon,
  CheckIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  StarIcon,
  RotateCcwIcon,
  PentagonIcon,
  MousePointerIcon,
  Loader2Icon,
} from 'lucide-react';
import {
  type SensorType,
  SENSOR_CONFIGS,
  getSensorIcon,
  getSensorColor,
} from '../utils/sensorUtils';
import {
  Point,
  getBoundingBox,
  getPolygonCentroid,
  pointsToSvgString,
  rectToPoints,
  isAxisAlignedRectangle,
  updateRectangleVertex,
  snapToOrtho,
  isPointInPolygon,
} from '../utils/polygonUtils';
import { thingsboardService } from '../services/thingsboard';
import { useTranslation } from 'react-i18next';

const SENSOR_TYPES = SENSOR_CONFIGS.filter(
  (st) => st.id !== 'CURTAIN' && st.id !== 'BLIND' && st.id !== 'HERV'
);

// Types
export type { SensorType };
export interface EditorSensor {
  id: string;
  type: SensorType;
  label: string;
  alias?: string;
  x: number;
  y: number;
  active: boolean;
  primary_appliance?: string;
}

export interface EditorZone {
  id: string;
  name: string;
  top: number;
  left: number;
  width: number;
  height: number;
  points: Point[];
  primary_sensor?: string | null;
  sensors: EditorSensor[];
}

interface FloorPlanEditorProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (
    zones: EditorZone[],
    assignedSensors: { originalZoneId: string | null; sensor: EditorSensor; newZoneId: string | null }[]
  ) => void;
  initialZones: EditorZone[];
  apartmentAssetId: string | undefined | null;
  floorPlanUrl: string | null;
  primaryZoneId?: string | null;
  onChangePrimaryZone?: (id: string | null) => void;
}

type DragState =
  | { type: 'none' }
  | { type: 'zone-move'; zoneId: string; startPos: Point; origPoints: Point[] }
  | {
      type: 'vertex-drag';
      zoneId: string;
      vertexIndex: number;
      origPoints: Point[];
      isFreeform?: boolean;
    }
  | {
      type: 'edge-drag';
      zoneId: string;
      edgeIndex: number;
      startPos: Point;
      origPoints: Point[];
      isHoriz: boolean;
      isVert: boolean;
    }
  | { type: 'sensor-drag'; sensor: EditorSensor; zoneId: string };

function getClientPos(
  e: React.MouseEvent | React.TouchEvent | TouchEvent | MouseEvent
) {
  if ('touches' in e) {
    const touch = e.touches[0] || (e as TouchEvent).changedTouches?.[0];
    if (!touch) return { clientX: 0, clientY: 0 };
    return { clientX: touch.clientX, clientY: touch.clientY };
  }
  return { clientX: (e as MouseEvent).clientX, clientY: (e as MouseEvent).clientY };
}

export function FloorPlanEditor({
  isOpen,
  onClose,
  onSave,
  initialZones,
  apartmentAssetId,
  floorPlanUrl,
  primaryZoneId: externalPrimaryZoneId,
  onChangePrimaryZone,
}: FloorPlanEditorProps) {
  const { t } = useTranslation();

  // Normalize initialZones so every zone has valid points
  const [zones, setZones] = useState<EditorZone[]>(() =>
    initialZones.map((z) => ({
      ...z,
      points:
        z.points && z.points.length >= 3
          ? z.points
          : rectToPoints(z.top || 0, z.left || 0, z.width || 20, z.height || 20),
    }))
  );

  const [localPrimaryZoneId, setLocalPrimaryZoneId] = useState<string | null>(
    externalPrimaryZoneId ?? null
  );

  useEffect(() => {
    setLocalPrimaryZoneId(externalPrimaryZoneId ?? null);
  }, [externalPrimaryZoneId]);

  const [availableSensors, setAvailableSensors] = useState<
    Record<string, { id: string; label: string }[]>
  >({ SEO: [], SER: [], SEL: [], SES: [], RADAR: [], SEC: [], SEV: [], HERV: [], SEM: [], SGL: [] });

  const [assignedSensors, setAssignedSensors] = useState<
    {
      originalZoneId: string | null;
      sensor: EditorSensor;
      newZoneId: string | null;
    }[]
  >([]);

  const [selectedZoneId, setSelectedZoneId] = useState<string | null>(null);
  const [selectedSensorId, setSelectedSensorId] = useState<string | null>(null);

  // Polygon Drawing state
  const [isDrawingMode, setIsDrawingMode] = useState(false);
  const [drawPoints, setDrawPoints] = useState<Point[]>([]);
  const [cursorPos, setCursorPos] = useState<Point | null>(null);

  // Drag state for moving zones, dragging vertices, or dragging sensors
  const [dragState, setDragState] = useState<DragState>({ type: 'none' });
  const [snapGuidelines, setSnapGuidelines] = useState<
    { orientation: 'horizontal' | 'vertical'; pos: number }[]
  >([]);

  // New Zone Creation Dialog
  const [newZoneName, setNewZoneName] = useState('');
  const [showNameInput, setShowNameInput] = useState(false);
  const [pendingZonePoints, setPendingZonePoints] = useState<Point[] | null>(null);

  // Sensor adding
  const [addingSensorType, setAddingSensorType] = useState<SensorType | null>(null);
  const [addingSensorLabel, setAddingSensorLabel] = useState<string | null>(null);
  const [expandedSensorType, setExpandedSensorType] = useState<SensorType | null>(null);

  const sensorListRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const imageWrapperRef = useRef<HTMLDivElement>(null);

  const [imageBounds, setImageBounds] = useState<{
    top: number;
    left: number;
    width: number;
    height: number;
  } | null>(null);

  const updateImageBounds = useCallback(() => {
    if (!imageRef.current || !imageWrapperRef.current) return;
    const img = imageRef.current;
    const wrapperRect = imageWrapperRef.current.getBoundingClientRect();
    const naturalW = img.naturalWidth || 1;
    const naturalH = img.naturalHeight || 1;
    const aspectRatio = naturalW / naturalH;
    const containerAspect = wrapperRect.width / wrapperRect.height;
    let renderedW: number, renderedH: number;
    if (aspectRatio > containerAspect) {
      renderedW = wrapperRect.width;
      renderedH = wrapperRect.width / aspectRatio;
    } else {
      renderedH = wrapperRect.height;
      renderedW = wrapperRect.height * aspectRatio;
    }
    setImageBounds({
      top: (wrapperRect.height - renderedH) / 2,
      left: (wrapperRect.width - renderedW) / 2,
      width: renderedW,
      height: renderedH,
    });
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const observer = new ResizeObserver(() => updateImageBounds());
    if (imageWrapperRef.current) observer.observe(imageWrapperRef.current);
    return () => observer.disconnect();
  }, [isOpen, updateImageBounds]);

  const fetchAvailableSensors = useCallback(async () => {
    if (!apartmentAssetId) return;
    try {
      const relations = await thingsboardService.getRelations('ASSET', apartmentAssetId);
      const devices = relations.filter(
        (r: any) => r.to.entityType === 'DEVICE' && r.type === 'Contains'
      );

      const newAvailableSensors: Record<string, { id: string; label: string }[]> = {
        SEO: [],
        SER: [],
        SEL: [],
        SES: [],
        RADAR: [],
        SEC: [],
        SEV: [],
        HERV: [],
        SEM: [],
        SGL: [],
      };

      for (const rel of devices) {
        try {
          const device = await thingsboardService.getDevice(rel.to.id);
          const type = device.type as string;
          if (!newAvailableSensors[type]) {
            newAvailableSensors[type] = [];
          }
          newAvailableSensors[type].push({
            id: device.id.id,
            label: device.label || device.name,
          });
        } catch (err) {
          console.error(`Failed to fetch device ${rel.to.id}`, err);
        }
      }
      setAvailableSensors(newAvailableSensors);
    } catch (error) {
      console.error('Failed to fetch available sensors:', error);
    }
  }, [apartmentAssetId]);

  const prevIsOpenRef = useRef(false);
  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current) {
      setZones(
        initialZones.map((z) => ({
          ...z,
          points:
            z.points && z.points.length >= 3
              ? z.points
              : rectToPoints(z.top || 0, z.left || 0, z.width || 20, z.height || 20),
        }))
      );
      setSelectedZoneId(null);
      setSelectedSensorId(null);
      setDragState({ type: 'none' });
      setIsDrawingMode(false);
      setDrawPoints([]);
      setAssignedSensors([]);
      fetchAvailableSensors();
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen, initialZones, fetchAvailableSensors]);

  // Convert screen coordinates to 0~100 percentage relative to floor plan image
  const getRelativePosition = useCallback(
    (clientX: number, clientY: number): Point => {
      if (!imageRef.current || !imageBounds || !imageWrapperRef.current) {
        return { x: 0, y: 0 };
      }
      const wrapperRect = imageWrapperRef.current.getBoundingClientRect();
      const imgLeft = wrapperRect.left + imageBounds.left;
      const imgTop = wrapperRect.top + imageBounds.top;
      return {
        x: Math.max(0, Math.min(100, ((clientX - imgLeft) / imageBounds.width) * 100)),
        y: Math.max(0, Math.min(100, ((clientY - imgTop) / imageBounds.height) * 100)),
      };
    },
    [imageBounds]
  );

  // Finish polygon drawing
  const finishPolygon = useCallback((pointsToFinish?: Point[]) => {
    const finalPoints = pointsToFinish || drawPoints;
    if (finalPoints.length < 3) return;

    setPendingZonePoints(finalPoints);
    setShowNameInput(true);
    setIsDrawingMode(false);
    setDrawPoints([]);
    setCursorPos(null);
  }, [drawPoints]);

  // Cancel polygon drawing
  const cancelDrawing = useCallback(() => {
    setIsDrawingMode(false);
    setDrawPoints([]);
    setCursorPos(null);
  }, []);

  // Undo last point
  const undoLastPoint = useCallback(() => {
    setDrawPoints((prev) => prev.slice(0, -1));
  }, []);

  // Place selected sensor into target zone
  const handlePlaceSensor = useCallback(
    (targetZoneId: string, pos: Point) => {
      if (!addingSensorType || !addingSensorLabel) return;
      const zone = zones.find((z) => z.id === targetZoneId);
      if (!zone) return;

      const relX = ((pos.x - zone.left) / (zone.width || 1)) * 100;
      const relY = ((pos.y - zone.top) / (zone.height || 1)) * 100;

      const newSensor: EditorSensor = {
        id:
          (availableSensors[addingSensorType] || []).find((s) => s.label === addingSensorLabel)?.id ||
          `${addingSensorType.toLowerCase()}-${Date.now()}`,
        type: addingSensorType,
        label: addingSensorLabel,
        x: Math.max(5, Math.min(95, relX)),
        y: Math.max(5, Math.min(95, relY)),
        active: true,
      };

      setAssignedSensors((prev) => [
        ...prev,
        { originalZoneId: null, sensor: newSensor, newZoneId: targetZoneId },
      ]);

      setZones((prev) =>
        prev.map((z) =>
          z.id === targetZoneId
            ? {
                ...z,
                sensors: [...z.sensors.filter((s) => s.id !== newSensor.id), newSensor],
              }
            : z
        )
      );

      setSelectedZoneId(targetZoneId);
      setSelectedSensorId(newSensor.id);
      setAddingSensorType(null);
      setAddingSensorLabel(null);
    },
    [addingSensorType, addingSensorLabel, zones, availableSensors]
  );

  // Handle canvas click / tap during drawing or placing sensors
  const handleCanvasClick = useCallback(
    (clientX: number, clientY: number) => {
      if (dragState.type !== 'none') return;
      const pos = getRelativePosition(clientX, clientY);

      if (isDrawingMode) {
        if (drawPoints.length >= 3) {
          // If clicked near first point, close polygon
          const firstPoint = drawPoints[0];
          const dist = Math.hypot(pos.x - firstPoint.x, pos.y - firstPoint.y);
          if (dist < 4) {
            finishPolygon(drawPoints);
            return;
          }
        }
        setDrawPoints((prev) => [...prev, pos]);
        return;
      }

      if (addingSensorType && addingSensorLabel) {
        let targetZone = zones.find((z) => isPointInPolygon(pos, z.points));
        if (!targetZone && selectedZoneId) {
          targetZone = zones.find((z) => z.id === selectedZoneId);
        }
        if (targetZone) {
          handlePlaceSensor(targetZone.id, pos);
          return;
        }
      }

      // Clicking empty canvas area deselects
      setSelectedZoneId(null);
      setSelectedSensorId(null);
    },
    [
      dragState.type,
      isDrawingMode,
      drawPoints,
      finishPolygon,
      addingSensorType,
      addingSensorLabel,
      selectedZoneId,
      getRelativePosition,
      zones,
      handlePlaceSensor,
    ]
  );

  // Pointer Move
  const handlePointerMove = useCallback(
    (clientX: number, clientY: number) => {
      const pos = getRelativePosition(clientX, clientY);

      if (isDrawingMode) {
        setCursorPos(pos);
        return;
      }

      if (dragState.type === 'vertex-drag') {
        const { zoneId, vertexIndex, isFreeform, origPoints } = dragState;
        const targetZone = zones.find((z) => z.id === zoneId);
        if (!targetZone) return;

        const basePoints = origPoints || targetZone.points;
        const isRect = !isFreeform && isAxisAlignedRectangle(basePoints);

        let newPoints: Point[];
        let snapLines: { orientation: 'horizontal' | 'vertical'; pos: number }[] = [];

        if (isRect) {
          // Maintain 90° orthogonal rectangle
          newPoints = updateRectangleVertex(basePoints, vertexIndex, pos);
          snapLines = [
            { orientation: 'vertical', pos: newPoints[vertexIndex].x },
            { orientation: 'horizontal', pos: newPoints[vertexIndex].y },
          ];
        } else {
          // Arbitrary polygon: magnetic snap to adjacent vertices
          const N = basePoints.length;
          const prevPt = basePoints[(vertexIndex + N - 1) % N];
          const nextPt = basePoints[(vertexIndex + 1) % N];
          const snapResult = snapToOrtho(pos, [prevPt, nextPt], 1.5);
          snapLines = snapResult.snapLines;

          newPoints = basePoints.map((p, idx) =>
            idx === vertexIndex
              ? {
                  x: Math.max(0, Math.min(100, snapResult.snappedPos.x)),
                  y: Math.max(0, Math.min(100, snapResult.snappedPos.y)),
                }
              : p
          );
        }

        setSnapGuidelines(snapLines);
        const bbox = getBoundingBox(newPoints);
        setZones((prev) =>
          prev.map((z) =>
            z.id === zoneId
              ? {
                  ...z,
                  points: newPoints,
                  top: bbox.top,
                  left: bbox.left,
                  width: bbox.width,
                  height: bbox.height,
                }
              : z
          )
        );
        return;
      }

      if (dragState.type === 'edge-drag') {
        const { zoneId, edgeIndex, startPos, origPoints, isHoriz, isVert } = dragState;
        const dx = pos.x - startPos.x;
        const dy = pos.y - startPos.y;
        const N = origPoints.length;
        const idx1 = edgeIndex;
        const idx2 = (edgeIndex + 1) % N;

        const isRect = isAxisAlignedRectangle(origPoints);
        const newPoints = origPoints.map((p) => ({ ...p }));
        let snapLines: { orientation: 'horizontal' | 'vertical'; pos: number }[] = [];

        if (isRect && (isHoriz || isVert)) {
          if (isHoriz) {
            const newY = Math.max(0, Math.min(100, origPoints[idx1].y + dy));
            newPoints[idx1].y = newY;
            newPoints[idx2].y = newY;
            snapLines = [{ orientation: 'horizontal', pos: newY }];
          } else {
            const newX = Math.max(0, Math.min(100, origPoints[idx1].x + dx));
            newPoints[idx1].x = newX;
            newPoints[idx2].x = newX;
            snapLines = [{ orientation: 'vertical', pos: newX }];
          }
        } else {
          newPoints[idx1] = {
            x: Math.max(0, Math.min(100, origPoints[idx1].x + dx)),
            y: Math.max(0, Math.min(100, origPoints[idx1].y + dy)),
          };
          newPoints[idx2] = {
            x: Math.max(0, Math.min(100, origPoints[idx2].x + dx)),
            y: Math.max(0, Math.min(100, origPoints[idx2].y + dy)),
          };
        }

        setSnapGuidelines(snapLines);
        const bbox = getBoundingBox(newPoints);
        setZones((prev) =>
          prev.map((z) =>
            z.id === zoneId
              ? {
                  ...z,
                  points: newPoints,
                  top: bbox.top,
                  left: bbox.left,
                  width: bbox.width,
                  height: bbox.height,
                }
              : z
          )
        );
        return;
      }

      if (dragState.type === 'zone-move') {
        const { zoneId, startPos, origPoints } = dragState;
        const dx = pos.x - startPos.x;
        const dy = pos.y - startPos.y;

        setZones((prev) =>
          prev.map((z) => {
            if (z.id !== zoneId) return z;
            // Check bounds for all points
            const shiftedPoints = origPoints.map((p) => ({
              x: Math.max(0, Math.min(100, p.x + dx)),
              y: Math.max(0, Math.min(100, p.y + dy)),
            }));
            const bbox = getBoundingBox(shiftedPoints);
            return {
              ...z,
              points: shiftedPoints,
              top: bbox.top,
              left: bbox.left,
              width: bbox.width,
              height: bbox.height,
            };
          })
        );
        return;
      }

      if (dragState.type === 'sensor-drag') {
        const { sensor, zoneId } = dragState;
        const targetZoneId = selectedZoneId || zoneId;
        const targetZone = zones.find((z) => z.id === targetZoneId);
        if (!targetZone) return;

        const relX = ((pos.x - targetZone.left) / targetZone.width) * 100;
        const relY = ((pos.y - targetZone.top) / targetZone.height) * 100;

        if (targetZoneId !== zoneId) {
          setAssignedSensors((prev) => {
            const existing = prev.find((a) => a.sensor.id === sensor.id);
            if (existing) {
              return [
                ...prev.filter((a) => a.sensor.id !== sensor.id),
                { ...existing, newZoneId: targetZoneId },
              ];
            }
            return [...prev, { originalZoneId: zoneId, sensor, newZoneId: targetZoneId }];
          });

          setZones((prev) =>
            prev.map((z) => {
              if (z.id === zoneId) {
                return { ...z, sensors: z.sensors.filter((s) => s.id !== sensor.id) };
              }
              if (z.id === targetZoneId) {
                return {
                  ...z,
                  sensors: [
                    ...z.sensors,
                    {
                      ...sensor,
                      x: Math.max(5, Math.min(95, relX)),
                      y: Math.max(5, Math.min(95, relY)),
                    },
                  ],
                };
              }
              return z;
            })
          );
          setDragState({ type: 'sensor-drag', sensor, zoneId: targetZoneId });
          return;
        }

        setZones((prev) =>
          prev.map((z) => {
            if (z.id !== zoneId) return z;
            return {
              ...z,
              sensors: z.sensors.map((s) =>
                s.id === sensor.id
                  ? {
                      ...s,
                      x: Math.max(5, Math.min(95, relX)),
                      y: Math.max(5, Math.min(95, relY)),
                    }
                  : s
              ),
            };
          })
        );
      }
    },
    [isDrawingMode, dragState, selectedZoneId, zones, getRelativePosition]
  );

  const handlePointerUp = useCallback(() => {
    setDragState({ type: 'none' });
    setSnapGuidelines([]);
  }, []);

  // Add vertex at midpoint
  const handleAddVertexAtMidpoint = useCallback(
    (zoneId: string, afterIndex: number, midpoint: Point, e: React.MouseEvent | React.TouchEvent) => {
      e.stopPropagation();
      setZones((prev) =>
        prev.map((z) => {
          if (z.id !== zoneId) return z;
          const newPoints = [...z.points];
          newPoints.splice(afterIndex + 1, 0, midpoint);
          const bbox = getBoundingBox(newPoints);
          return {
            ...z,
            points: newPoints,
            top: bbox.top,
            left: bbox.left,
            width: bbox.width,
            height: bbox.height,
          };
        })
      );
      // Immediately start dragging this new vertex
      setDragState({ type: 'vertex-drag', zoneId, vertexIndex: afterIndex + 1 });
    },
    []
  );

  // Delete vertex (right click or Alt+click)
  const handleDeleteVertex = useCallback((zoneId: string, vertexIndex: number, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setZones((prev) =>
      prev.map((z) => {
        if (z.id !== zoneId) return z;
        if (z.points.length <= 3) return z; // Maintain at least 3 points
        const newPoints = z.points.filter((_, idx) => idx !== vertexIndex);
        const bbox = getBoundingBox(newPoints);
        return {
          ...z,
          points: newPoints,
          top: bbox.top,
          left: bbox.left,
          width: bbox.width,
          height: bbox.height,
        };
      })
    );
  }, []);

  // Keyboard shortcut listener (Escape to cancel drawing, Enter to finish, Backspace/Delete to undo point)
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (isDrawingMode) {
          cancelDrawing();
        } else {
          setSelectedZoneId(null);
          setSelectedSensorId(null);
        }
      } else if (e.key === 'Enter') {
        if (isDrawingMode && drawPoints.length >= 3) {
          finishPolygon();
        }
      } else if (e.key === 'Backspace' || (e.ctrlKey && e.key === 'z')) {
        if (isDrawingMode && drawPoints.length > 0) {
          e.preventDefault();
          undoLastPoint();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isDrawingMode, drawPoints, cancelDrawing, finishPolygon, undoLastPoint]);

  // Confirm new zone addition
  const handleAddZoneConfirm = () => {
    if (!pendingZonePoints || !newZoneName.trim()) return;

    const bbox = getBoundingBox(pendingZonePoints);
    const newZone: EditorZone = {
      id: `zone-${Date.now()}`,
      name: newZoneName.trim(),
      top: bbox.top,
      left: bbox.left,
      width: bbox.width,
      height: bbox.height,
      points: pendingZonePoints,
      sensors: [],
    };

    setZones((prev) => [...prev, newZone]);
    setSelectedZoneId(newZone.id);
    setPendingZonePoints(null);
    setNewZoneName('');
    setShowNameInput(false);
  };

  // Delete Zone
  const handleDeleteZone = (zoneId: string) => {
    const zoneToDelete = zones.find((z) => z.id === zoneId);
    if (zoneToDelete && zoneToDelete.sensors.length > 0) {
      zoneToDelete.sensors.forEach((sensor) => {
        setAssignedSensors((prev) => {
          const filtered = prev.filter((a) => a.sensor.id !== sensor.id);
          return [...filtered, { originalZoneId: zoneId, sensor, newZoneId: null }];
        });

        setAvailableSensors((prev) => {
          const type = sensor.type;
          if (!prev[type].some((s) => s.id === sensor.id)) {
            return {
              ...prev,
              [type]: [...prev[type], { id: sensor.id, label: sensor.label }],
            };
          }
          return prev;
        });
      });
    }

    setZones((prev) => prev.filter((z) => z.id !== zoneId));
    if (selectedZoneId === zoneId) setSelectedZoneId(null);
  };

  // Delete Sensor
  const handleDeleteSensor = (zoneId: string, sensorId: string) => {
    const sensorToDelete = zones.find((z) => z.id === zoneId)?.sensors.find((s) => s.id === sensorId);

    if (sensorToDelete) {
      setAssignedSensors((prev) => {
        const filtered = prev.filter((a) => a.sensor.id !== sensorId);
        return [...filtered, { originalZoneId: zoneId, sensor: sensorToDelete, newZoneId: null }];
      });

      setAvailableSensors((prev) => {
        const type = sensorToDelete.type;
        const currentList = prev[type] || [];
        if (!currentList.some((s) => s.id === sensorToDelete.id)) {
          return {
            ...prev,
            [type]: [...currentList, { id: sensorToDelete.id, label: sensorToDelete.label }],
          };
        }
        return prev;
      });
    }

    setZones((prev) =>
      prev.map((z) =>
        z.id === zoneId
          ? {
              ...z,
              sensors: z.sensors.filter((s) => s.id !== sensorId),
            }
          : z
      )
    );
    if (selectedSensorId === sensorId) setSelectedSensorId(null);
  };

  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    if (isSaving) return;
    try {
      setIsSaving(true);
      const finalZones = zones.map((z) => {
        const bbox = getBoundingBox(z.points);
        return {
          ...z,
          top: bbox.top,
          left: bbox.left,
          width: bbox.width,
          height: bbox.height,
        };
      });
      await onSave(finalZones, assignedSensors);
      onClose();
    } catch (err: any) {
      console.error('Failed to save floor plan mapping:', err);
      alert('저장 중 오류가 발생했습니다: ' + (err?.message || String(err)));
    } finally {
      setIsSaving(false);
    }
  };

  const handleTogglePrimary = (zoneId: string) => {
    const next = localPrimaryZoneId === zoneId ? null : zoneId;
    setLocalPrimaryZoneId(next);
    onChangePrimaryZone?.(next);
  };

  const handleTogglePrimarySensor = (zoneId: string, sensorId: string) => {
    setZones((prev) =>
      prev.map((z) => {
        if (z.id === zoneId) {
          return {
            ...z,
            primary_sensor: z.primary_sensor === sensorId ? null : sensorId,
          };
        }
        return z;
      })
    );
  };

  const scrollSensorList = (dir: 'up' | 'down') => {
    if (!sensorListRef.current) return;
    sensorListRef.current.scrollBy({
      top: dir === 'down' ? 40 : -40,
      behavior: 'smooth',
    });
  };

  const selectedZone = zones.find((z) => z.id === selectedZoneId);

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] bg-[#0a0f1a] flex flex-col select-none"
    >
      {/* Top Toolbar */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-white/20 bg-[#0a0f1a]/90 backdrop-blur-md">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <PentagonIcon className="w-6 h-6 text-cyan-400" />
            <h1 className="text-xl font-bold text-white">
              {t('dashboard.edit_floorplan', '평면도 폴리곤 영역 편집')}
            </h1>
          </div>
          <div className="h-6 w-px bg-white/10" />
          <span className="text-sm text-slate-400 font-roboto">
            {t('dashboard.zone_count', { count: zones.length, defaultValue: '{{count}}개 영역' })}
          </span>
        </div>

        <div className="flex items-center gap-3">
          {/* Polygon Drawing mode toolbar */}
          {isDrawingMode ? (
            <div className="flex items-center gap-2 bg-cyan-950/60 border border-cyan-500/40 px-3 py-1.5 rounded-xl">
              <span className="text-xs font-semibold text-cyan-300">
                점 찍는 중 ({drawPoints.length}개)
              </span>

              <button
                onClick={undoLastPoint}
                disabled={drawPoints.length === 0}
                className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-white/10 hover:bg-white/20 text-xs text-slate-300 disabled:opacity-40 transition-all"
                title="마지막 점 되돌리기 (Backspace / Ctrl+Z)"
              >
                <RotateCcwIcon className="w-3.5 h-3.5" />
                되돌리기
              </button>

              <button
                onClick={() => finishPolygon()}
                disabled={drawPoints.length < 3}
                className="flex items-center gap-1 px-3 py-1 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold text-xs shadow-lg disabled:opacity-40 disabled:pointer-events-none transition-all"
                title="다각형 닫고 영역 생성 (Enter)"
              >
                <CheckIcon className="w-3.5 h-3.5" />
                영역 완성 ({drawPoints.length >= 3 ? '완료' : '최소 3개'})
              </button>

              <button
                onClick={cancelDrawing}
                className="p-1 rounded-lg hover:bg-red-500/20 text-slate-400 hover:text-red-300 transition-colors"
                title="그리기 취소 (Esc)"
              >
                <XIcon className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => {
                setIsDrawingMode(true);
                setDrawPoints([]);
                setSelectedZoneId(null);
                setSelectedSensorId(null);
                setAddingSensorType(null);
              }}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-cyan-500/20 border border-cyan-500/50 text-cyan-300 hover:bg-cyan-500/30 transition-all shadow-[0_0_15px_rgba(6,182,212,0.2)]"
            >
              <PlusIcon className="w-4 h-4" />
              {t('dashboard.add_zone', '새 영역 그리기 (폴리곤)')}
            </button>
          )}

          <div className="h-6 w-px bg-white/10" />

          <button
            onClick={handleSave}
            disabled={isSaving}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 text-sm font-semibold hover:bg-emerald-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-[0_0_15px_rgba(16,185,129,0.2)]"
          >
            {isSaving ? (
              <Loader2Icon className="w-4 h-4 animate-spin text-emerald-300" />
            ) : (
              <SaveIcon className="w-4 h-4" />
            )}
            {isSaving ? '저장 중...' : t('common.save', '저장')}
          </button>

          <button
            onClick={onClose}
            className="p-2 rounded-lg bg-white/5 border border-white/20 text-slate-400 hover:text-white hover:bg-white/10 transition-all"
          >
            <XIcon className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel: Zone List */}
        <div className="w-64 border-r border-white/20 bg-[#0a0f1a]/60 flex flex-col">
          <div className="p-4 border-b border-white/20 flex items-center justify-between">
            <h2 className="text-[0.9rem] font-bold text-slate-300 uppercase tracking-wider">
              {t('dashboard.zone_list', '영역 목록')}
            </h2>
            <span className="text-xs text-cyan-400 font-mono font-bold">
              {zones.length}
            </span>
          </div>

          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {zones.map((zone) => {
              const isSelected = selectedZoneId === zone.id;
              return (
                <div
                  key={zone.id}
                  onClick={() => {
                    setSelectedZoneId(zone.id);
                    setIsDrawingMode(false);
                    setAddingSensorType(null);
                  }}
                  className={`w-full text-left p-3 rounded-xl transition-all cursor-pointer ${
                    isSelected
                      ? 'bg-cyan-500/20 border border-cyan-500/50 shadow-[0_0_15px_rgba(6,182,212,0.2)]'
                      : 'bg-white/5 border border-white/10 hover:bg-white/10'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span
                      className={`font-semibold ${
                        isSelected ? 'text-cyan-300' : 'text-slate-200'
                      }`}
                    >
                      {zone.name}
                    </span>
                    <div className="flex items-center gap-1">
                      {/* 대표공간 지정 */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleTogglePrimary(zone.id);
                        }}
                        className={`p-1 rounded transition-colors ${
                          localPrimaryZoneId === zone.id
                            ? 'text-amber-400 hover:text-amber-300'
                            : 'text-slate-600 hover:text-amber-400'
                        }`}
                        title="대표공간으로 지정"
                      >
                        <StarIcon
                          className="w-3.5 h-3.5"
                          fill={localPrimaryZoneId === zone.id ? 'currentColor' : 'none'}
                          strokeWidth={localPrimaryZoneId === zone.id ? 0 : 1.5}
                        />
                      </button>

                      {/* 삭제 */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteZone(zone.id);
                        }}
                        className="p-1 rounded hover:bg-red-500/20 text-slate-500 hover:text-red-400 transition-colors"
                        title="영역 삭제"
                      >
                        <Trash2Icon className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>

                  <div className="text-xs text-slate-400 font-roboto mt-1 flex items-center justify-between">
                    <span>
                      {t('dashboard.sensor_count', {
                        count: zone.sensors.length,
                        defaultValue: '{{count}}개 센서',
                      })}
                    </span>
                    <span className="text-[0.7rem] text-slate-500 font-mono">
                      꼭짓점 {zone.points?.length || 4}개
                    </span>
                  </div>

                  {localPrimaryZoneId === zone.id && (
                    <div className="mt-1 text-[0.7rem] text-amber-400 font-bold flex items-center gap-1">
                      ★ {t('dashboard.primary_zone', '대표공간')}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Main Floor Plan Canvas */}
        <div
          ref={containerRef}
          className={`flex-1 relative overflow-hidden bg-slate-950 flex items-center justify-center ${
            isDrawingMode ? 'cursor-crosshair' : ''
          } ${addingSensorType ? 'cursor-cell' : ''}`}
          onMouseMove={(e) => handlePointerMove(e.clientX, e.clientY)}
          onMouseUp={handlePointerUp}
          onTouchMove={(e) => {
            const p = getClientPos(e);
            handlePointerMove(p.clientX, p.clientY);
          }}
          onTouchEnd={handlePointerUp}
        >
          <div className="absolute inset-4">
            <div
              ref={imageWrapperRef}
              className="relative w-full h-full"
              onClick={(e) => handleCanvasClick(e.clientX, e.clientY)}
            >
              {/* Floor Plan Image */}
              {floorPlanUrl ? (
                <img
                  ref={imageRef}
                  src={floorPlanUrl}
                  alt="Floor Plan Base"
                  className="absolute inset-0 w-full h-full object-contain opacity-50 pointer-events-none select-none"
                  crossOrigin="anonymous"
                  draggable={false}
                  onLoad={updateImageBounds}
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center text-slate-500 opacity-50 pointer-events-none">
                  평면도 로딩 중이거나 이미지가 없습니다.
                </div>
              )}

              {/* SVG Polygon & Editing Overlay */}
              {imageBounds && (
                <div
                  className="absolute"
                  style={{
                    top: `${imageBounds.top}px`,
                    left: `${imageBounds.left}px`,
                    width: `${imageBounds.width}px`,
                    height: `${imageBounds.height}px`,
                  }}
                >
                  <svg
                    className="absolute inset-0 w-full h-full pointer-events-none overflow-visible"
                    viewBox="0 0 100 100"
                    preserveAspectRatio="none"
                  >
                    {/* Render Zones */}
                    {zones.map((zone) => {
                      const isSelected = selectedZoneId === zone.id;
                      const centroid = getPolygonCentroid(zone.points);
                      const pointsStr = pointsToSvgString(zone.points);

                      return (
                        <g key={zone.id}>
                          {/* Polygon Body */}
                          <polygon
                            points={pointsStr}
                            className="pointer-events-auto cursor-pointer transition-colors duration-150"
                            fill={
                              isSelected
                                ? 'rgba(6, 182, 212, 0.28)'
                                : 'rgba(30, 41, 59, 0.45)'
                            }
                            stroke={isSelected ? '#22d3ee' : 'rgba(255, 255, 255, 0.4)'}
                            strokeWidth={isSelected ? '0.6' : '0.35'}
                            strokeDasharray={isSelected ? 'none' : '1.5 1'}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (addingSensorType && addingSensorLabel) {
                                const pos = getRelativePosition(e.clientX, e.clientY);
                                handlePlaceSensor(zone.id, pos);
                                return;
                              }
                              if (!isDrawingMode) {
                                setSelectedZoneId(zone.id);
                                setSelectedSensorId(null);
                              }
                            }}
                            onMouseDown={(e) => {
                              if (isDrawingMode || (addingSensorType && addingSensorLabel)) return;
                              e.stopPropagation();
                              setSelectedZoneId(zone.id);
                              setSelectedSensorId(null);
                              const pos = getRelativePosition(e.clientX, e.clientY);
                              setDragState({
                                type: 'zone-move',
                                zoneId: zone.id,
                                startPos: pos,
                                origPoints: [...zone.points],
                              });
                            }}
                            onTouchStart={(e) => {
                              if (isDrawingMode || (addingSensorType && addingSensorLabel)) return;
                              e.stopPropagation();
                              setSelectedZoneId(zone.id);
                              setSelectedSensorId(null);
                              const p = getClientPos(e);
                              const pos = getRelativePosition(p.clientX, p.clientY);
                              setDragState({
                                type: 'zone-move',
                                zoneId: zone.id,
                                startPos: pos,
                                origPoints: [...zone.points],
                              });
                            }}
                          />

                          {/* Selected Zone: Edge Resize Drag Handles (Parallel Moving) */}
                          {isSelected &&
                            zone.points.map((p1, idx) => {
                              const p2 = zone.points[(idx + 1) % zone.points.length];
                              const isHoriz = Math.abs(p1.y - p2.y) <= 3.0;
                              const isVert = Math.abs(p1.x - p2.x) <= 3.0;
                              const cursor = isHoriz
                                ? 'cursor-ns-resize'
                                : isVert
                                ? 'cursor-ew-resize'
                                : 'cursor-move';

                              return (
                                <g key={`edge-${idx}`} className="group/edge">
                                  {/* Invisible wider hit line */}
                                  <line
                                    x1={p1.x}
                                    y1={p1.y}
                                    x2={p2.x}
                                    y2={p2.y}
                                    stroke="transparent"
                                    strokeWidth="3.2"
                                    className={`pointer-events-auto ${cursor}`}
                                    onClick={(e) => e.stopPropagation()}
                                    onMouseDown={(e) => {
                                      e.stopPropagation();
                                      const pos = getRelativePosition(e.clientX, e.clientY);
                                      setDragState({
                                        type: 'edge-drag',
                                        zoneId: zone.id,
                                        edgeIndex: idx,
                                        startPos: pos,
                                        origPoints: [...zone.points],
                                        isHoriz,
                                        isVert,
                                      });
                                    }}
                                    onTouchStart={(e) => {
                                      e.stopPropagation();
                                      const p = getClientPos(e);
                                      const pos = getRelativePosition(p.clientX, p.clientY);
                                      setDragState({
                                        type: 'edge-drag',
                                        zoneId: zone.id,
                                        edgeIndex: idx,
                                        startPos: pos,
                                        origPoints: [...zone.points],
                                        isHoriz,
                                        isVert,
                                      });
                                    }}
                                  />
                                  {/* Visible edge glow on hover */}
                                  <line
                                    x1={p1.x}
                                    y1={p1.y}
                                    x2={p2.x}
                                    y2={p2.y}
                                    stroke="#22d3ee"
                                    strokeWidth="0.8"
                                    strokeOpacity="0"
                                    className="pointer-events-none transition-all group-hover/edge:stroke-opacity-100"
                                  />
                                </g>
                              );
                            })}

                          {/* Selected Zone: Midpoint "+" insertion handles */}
                          {isSelected &&
                            zone.points.map((p1, idx) => {
                              const p2 = zone.points[(idx + 1) % zone.points.length];
                              const mid = {
                                x: (p1.x + p2.x) / 2,
                                y: (p1.y + p2.y) / 2,
                              };
                              return (
                                <g
                                  key={`mid-${idx}`}
                                  className="pointer-events-auto cursor-copy group/mid"
                                  onClick={(e) => e.stopPropagation()}
                                  onMouseDown={(e) =>
                                    handleAddVertexAtMidpoint(zone.id, idx, mid, e)
                                  }
                                  onTouchStart={(e) =>
                                    handleAddVertexAtMidpoint(zone.id, idx, mid, e)
                                  }
                                >
                                  {/* Invisible larger hit area */}
                                  <circle
                                    cx={mid.x}
                                    cy={mid.y}
                                    r="2.5"
                                    fill="transparent"
                                  />
                                  {/* Visible midpoint indicator */}
                                  <circle
                                    cx={mid.x}
                                    cy={mid.y}
                                    r="0.9"
                                    className="fill-cyan-500/70 stroke-white group-hover/mid:fill-cyan-300 group-hover/mid:stroke-cyan-100"
                                    strokeWidth="0.25"
                                  />
                                </g>
                              );
                            })}

                          {/* Selected Zone: Vertex Drag Handles */}
                          {isSelected &&
                            zone.points.map((pt, idx) => (
                              <g
                                key={`vertex-${idx}`}
                                className="pointer-events-auto cursor-grab active:cursor-grabbing group/vtx"
                                onClick={(e) => e.stopPropagation()}
                                onMouseDown={(e) => {
                                  e.stopPropagation();
                                  if (e.altKey || e.button === 2) {
                                    handleDeleteVertex(zone.id, idx, e);
                                    return;
                                  }
                                  setDragState({
                                    type: 'vertex-drag',
                                    zoneId: zone.id,
                                    vertexIndex: idx,
                                    origPoints: [...zone.points],
                                    isFreeform: e.shiftKey,
                                  });
                                }}
                                onContextMenu={(e) => handleDeleteVertex(zone.id, idx, e)}
                                onTouchStart={(e) => {
                                  e.stopPropagation();
                                  setDragState({
                                    type: 'vertex-drag',
                                    zoneId: zone.id,
                                    vertexIndex: idx,
                                    origPoints: [...zone.points],
                                  });
                                }}
                              >
                                {/* Invisible larger hit area for easy grabbing without jitter */}
                                <circle
                                  cx={pt.x}
                                  cy={pt.y}
                                  r="3.2"
                                  fill="transparent"
                                />
                                {/* Visible vertex circle with color hover effect only (no scale shift) */}
                                <circle
                                  cx={pt.x}
                                  cy={pt.y}
                                  r="1.3"
                                  className="fill-cyan-400 stroke-white group-hover/vtx:fill-white group-hover/vtx:stroke-cyan-400 drop-shadow"
                                  strokeWidth="0.35"
                                />
                              </g>
                            ))}
                        </g>
                      );
                    })}

                    {/* Snap Guidelines */}
                    {snapGuidelines.map((g, idx) =>
                      g.orientation === 'horizontal' ? (
                        <line
                          key={`snap-h-${idx}`}
                          x1="0"
                          y1={g.pos}
                          x2="100"
                          y2={g.pos}
                          stroke="#38bdf8"
                          strokeWidth="0.3"
                          strokeDasharray="1 1"
                          className="pointer-events-none opacity-80"
                        />
                      ) : (
                        <line
                          key={`snap-v-${idx}`}
                          x1={g.pos}
                          y1="0"
                          x2={g.pos}
                          y2="100"
                          stroke="#38bdf8"
                          strokeWidth="0.3"
                          strokeDasharray="1 1"
                          className="pointer-events-none opacity-80"
                        />
                      )
                    )}

                    {/* Active Drawing Preview */}
                    {isDrawingMode && drawPoints.length > 0 && (
                      <g>
                        {/* Polyline / Polygon preview */}
                        <polyline
                          points={
                            cursorPos
                              ? `${pointsToSvgString(drawPoints)} ${cursorPos.x.toFixed(2)},${cursorPos.y.toFixed(2)}`
                              : pointsToSvgString(drawPoints)
                          }
                          fill="none"
                          stroke="#38bdf8"
                          strokeWidth="0.5"
                          strokeDasharray="1.2 0.8"
                        />

                        {/* Fill area when >= 3 points */}
                        {drawPoints.length >= 3 && cursorPos && (
                          <polygon
                            points={`${pointsToSvgString(drawPoints)} ${cursorPos.x.toFixed(2)},${cursorPos.y.toFixed(2)}`}
                            fill="rgba(56, 189, 248, 0.15)"
                            stroke="none"
                          />
                        )}

                        {/* Vertices of current drawing */}
                        {drawPoints.map((pt, idx) => {
                          const isStart = idx === 0;
                          return (
                            <g
                              key={`draw-pt-${idx}`}
                              className="pointer-events-auto cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (isStart && drawPoints.length >= 3) {
                                  finishPolygon();
                                }
                              }}
                            >
                              {isStart && (
                                <circle
                                  cx={pt.x}
                                  cy={pt.y}
                                  r="3.5"
                                  fill="transparent"
                                />
                              )}
                              <circle
                                cx={pt.x}
                                cy={pt.y}
                                r={isStart ? 1.6 : 1.1}
                                className={
                                  isStart
                                    ? 'fill-amber-400 stroke-white'
                                    : 'fill-cyan-400 stroke-slate-900'
                                }
                                strokeWidth="0.3"
                              />
                            </g>
                          );
                        })}
                      </g>
                    )}
                  </svg>

                  {/* HTML Labels and Sensors layer */}
                  {zones.map((zone) => {
                    const centroid = getPolygonCentroid(zone.points);
                    const isSelected = selectedZoneId === zone.id;

                    return (
                      <React.Fragment key={`html-${zone.id}`}>
                        {/* Zone Centroid Badge */}
                        <div
                          className="absolute pointer-events-auto -translate-x-1/2 -translate-y-1/2 transition-transform cursor-pointer"
                          style={{
                            left: `${centroid.x}%`,
                            top: `${centroid.y}%`,
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (addingSensorType && addingSensorLabel) {
                              const pos = getRelativePosition(e.clientX, e.clientY);
                              handlePlaceSensor(zone.id, pos);
                              return;
                            }
                            if (!isDrawingMode) {
                              setSelectedZoneId(zone.id);
                              setSelectedSensorId(null);
                            }
                          }}
                        >
                          <div
                            className={`px-2 py-0.5 rounded-md text-[0.75rem] font-bold uppercase tracking-wider backdrop-blur-md shadow-md ${
                              isSelected
                                ? 'bg-cyan-500/80 text-slate-950 ring-2 ring-cyan-300'
                                : 'bg-slate-900/75 text-slate-200 border border-white/20'
                            }`}
                          >
                            {zone.name}
                          </div>
                        </div>

                        {/* Sensors */}
                        {zone.sensors.map((sensor) => {
                          const Icon = getSensorIcon(sensor.type);
                          const colorClasses = getSensorColor(sensor.type);
                          const isSel = selectedSensorId === sensor.id;

                          // Compute absolute position from zone bbox
                          const absLeft = zone.left + (sensor.x * zone.width) / 100;
                          const absTop = zone.top + (sensor.y * zone.height) / 100;

                          return (
                            <div
                              key={sensor.id}
                              className={`absolute cursor-move select-none ${
                                isSel ? 'z-30' : 'z-20'
                              }`}
                              style={{
                                left: `${absLeft}%`,
                                top: `${absTop}%`,
                                transform: 'translate(-50%, -50%)',
                              }}
                              onClick={(e) => e.stopPropagation()}
                              onMouseDown={(e) => {
                                e.stopPropagation();
                                setSelectedSensorId(sensor.id);
                                setDragState({
                                  type: 'sensor-drag',
                                  sensor,
                                  zoneId: zone.id,
                                });
                              }}
                              onTouchStart={(e) => {
                                e.stopPropagation();
                                setSelectedSensorId(sensor.id);
                                setDragState({
                                  type: 'sensor-drag',
                                  sensor,
                                  zoneId: zone.id,
                                });
                              }}
                            >
                              <div
                                className={`relative w-7 h-7 rounded-full flex items-center justify-center border-2 shadow-lg transition-all ${colorClasses} ${
                                  isSel
                                    ? 'scale-125 ring-2 ring-white shadow-[0_0_15px_rgba(255,255,255,0.6)]'
                                    : 'hover:scale-110'
                                }`}
                              >
                                <Icon className="w-3.5 h-3.5" />
                              </div>
                              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 px-1.5 py-0.5 bg-black/90 backdrop-blur-sm rounded text-[0.6rem] leading-tight font-roboto text-white whitespace-nowrap pointer-events-none shadow-md">
                                {sensor.label}
                              </div>
                            </div>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* User hint overlay */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 pointer-events-none">
            {isDrawingMode ? (
              <div className="px-4 py-2 bg-slate-900/90 backdrop-blur-md rounded-full border border-cyan-500/40 text-xs text-cyan-200 shadow-2xl flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-cyan-400 animate-ping" />
                <span>
                  {drawPoints.length === 0
                    ? '평면도를 클릭하여 다각형의 첫 번째 꼭짓점을 찍으세요'
                    : drawPoints.length < 3
                    ? `점을 계속 찍어주세요 (${drawPoints.length}/3)`
                    : '시작점(노란색 점)을 클릭하거나 [영역 완성]을 눌러 다각형을 완성하세요'}
                </span>
              </div>
            ) : addingSensorLabel ? (
              <div className="px-4 py-2 bg-slate-900/90 backdrop-blur-md rounded-full border border-amber-500/40 text-xs text-amber-200 shadow-2xl">
                클릭하여 {addingSensorLabel} 센서를 배치하세요
              </div>
            ) : selectedZone ? (
              <div className="px-4 py-1.5 bg-slate-900/90 backdrop-blur-md rounded-full border border-cyan-500/30 text-[0.75rem] text-slate-200 shadow-xl flex items-center gap-3">
                <span className="font-semibold text-cyan-400">변(선분) 드래그:</span>
                <span>상하/좌우 평행 조절</span>
                <span className="text-white/20">•</span>
                <span className="font-semibold text-cyan-400">꼭짓점 드래그:</span>
                <span>직교 사각형 크기 조절 (Shift: 자유 다각형)</span>
                <span className="text-white/20">•</span>
                <span className="font-semibold text-cyan-400">중간점:</span>
                <span>새 점 추가</span>
                <span className="text-white/20">•</span>
                <span className="font-semibold text-cyan-400">우클릭:</span>
                <span>점 삭제</span>
              </div>
            ) : null}
          </div>
        </div>

        {/* Right Panel: Sensor Management (Persistent Width to prevent Canvas layout shift) */}
        <div className="w-72 border-l border-white/20 bg-[#0a0f1a]/60 flex flex-col flex-shrink-0">
          {selectedZone ? (
            <>
              <div className="p-4 border-b border-white/20">
                <h2 className="text-[0.9rem] font-bold text-slate-300 uppercase tracking-wider">
                  센서 관리
                </h2>
                <p className="text-xs text-cyan-400 mt-1 font-semibold">{selectedZone.name}</p>
              </div>

              {/* Add Sensor Palette */}
              <div className="p-3 border-b border-white/20">
                <div className="text-xs text-slate-400 uppercase tracking-wider font-bold mb-2">
                  센서 추가
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {SENSOR_TYPES.map((st) => {
                    const Icon = st.icon;
                    const isExpanded = expandedSensorType === st.id;
                    return (
                      <button
                        key={st.id}
                        onClick={() => {
                          setExpandedSensorType(isExpanded ? null : st.id);
                          if (!isExpanded) {
                            setAddingSensorType(null);
                            setAddingSensorLabel(null);
                          }
                        }}
                        className={`flex flex-col items-center gap-1 p-2 rounded-lg transition-all ${
                          isExpanded
                            ? `${st.color} border shadow-md`
                            : 'bg-white/5 border border-white/10 text-slate-400 hover:bg-white/10'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        <span className="text-[0.75rem] font-bold">{st.id}</span>
                      </button>
                    );
                  })}
                </div>

                {/* Sensor selection dropdown */}
                <AnimatePresence>
                  {expandedSensorType && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.15 }}
                      className="overflow-hidden mt-2"
                    >
                      <div className="rounded-lg border border-white/20 bg-black/40 overflow-hidden">
                        {(() => {
                          const st = SENSOR_TYPES.find((s) => s.id === expandedSensorType)!;
                          const Icon = st.icon;
                          const usedLabels = new Set(
                            zones.flatMap((z) => z.sensors).map((s) => s.label)
                          );
                          const available = (availableSensors[expandedSensorType] || []).filter(
                            (i) => !usedLabels.has(i.label)
                          );

                          if (available.length === 0) {
                            return (
                              <div className="px-3 py-2 text-[0.7rem] text-slate-500 text-center">
                                사용 가능한 센서가 없습니다
                              </div>
                            );
                          }

                          return (
                            <div className="relative h-28">
                              <div
                                ref={sensorListRef}
                                className="h-full overflow-y-auto pr-6 sensor-list-scroll"
                              >
                                {available.map((sensorItem) => {
                                  const label = sensorItem.label;
                                  const isSelected =
                                    addingSensorLabel === label &&
                                    addingSensorType === expandedSensorType;
                                  return (
                                    <button
                                      key={label}
                                      onClick={() => {
                                        if (isSelected) {
                                          setAddingSensorType(null);
                                          setAddingSensorLabel(null);
                                        } else {
                                          setAddingSensorType(expandedSensorType);
                                          setAddingSensorLabel(label);
                                        }
                                      }}
                                      className={`w-full flex items-center gap-2 px-3 py-2 text-left transition-all border-b border-white/10 last:border-0 ${
                                        isSelected
                                          ? `${st.color} bg-opacity-20`
                                          : 'hover:bg-white/5 text-slate-300'
                                      }`}
                                    >
                                      <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                                      <span className="text-[0.75rem] font-bold flex-1 truncate">
                                        {label}
                                      </span>
                                      {isSelected && (
                                        <span className="text-[0.6rem] font-bold uppercase tracking-wider text-cyan-400">
                                          선택됨
                                        </span>
                                      )}
                                    </button>
                                  );
                                })}
                              </div>

                              <div className="absolute right-0 top-0 bottom-0 w-6 flex flex-col border-l border-white/10 bg-white/5">
                                <button
                                  onClick={() => scrollSensorList('up')}
                                  className="flex-1 flex items-center justify-center text-slate-400 hover:text-white"
                                >
                                  <ChevronUpIcon className="w-3 h-3" />
                                </button>
                                <button
                                  onClick={() => scrollSensorList('down')}
                                  className="flex-1 flex items-center justify-center text-slate-400 hover:text-white border-t border-white/10"
                                >
                                  <ChevronDownIcon className="w-3 h-3" />
                                </button>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Installed Sensors in selected zone */}
              <div className="p-3 border-b border-white/20 flex-1 overflow-y-auto">
                <div className="text-xs text-slate-400 uppercase tracking-wider font-bold mb-2">
                  배치된 센서 ({selectedZone.sensors.length})
                </div>

                {selectedZone.sensors.length === 0 ? (
                  <div className="text-xs text-slate-600 text-center py-4">
                    배치된 센서가 없습니다
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    {selectedZone.sensors.map((sensor) => {
                      const Icon = getSensorIcon(sensor.type);
                      const isPrimary = selectedZone.primary_sensor === sensor.id;
                      return (
                        <div
                          key={sensor.id}
                          className="flex items-center justify-between p-2 rounded-lg bg-white/5 border border-white/10"
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <Icon className="w-3.5 h-3.5 text-cyan-400 flex-shrink-0" />
                            <span className="text-xs font-semibold text-slate-200 truncate">
                              {sensor.label}
                            </span>
                          </div>

                          <div className="flex items-center gap-1">
                            {/* 대표센서 지정 */}
                            <button
                              onClick={() =>
                                handleTogglePrimarySensor(selectedZone.id, sensor.id)
                              }
                              className={`p-1 rounded text-xs ${
                                isPrimary
                                  ? 'text-amber-400 font-bold'
                                  : 'text-slate-600 hover:text-amber-400'
                              }`}
                              title="대표 센서로 지정"
                            >
                              ★
                            </button>

                            {/* 센서 제거 */}
                            <button
                              onClick={() => handleDeleteSensor(selectedZone.id, sensor.id)}
                              className="p-1 text-slate-600 hover:text-red-400 transition-colors"
                              title="센서 제거"
                            >
                              <Trash2Icon className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center p-6 text-center text-slate-500">
              <div className="w-12 h-12 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center mb-3 text-slate-400 shadow-inner">
                <PentagonIcon className="w-6 h-6 text-cyan-400/60" />
              </div>
              <div className="text-sm font-semibold text-slate-300 mb-1">
                선택된 영역 없음
              </div>
              <p className="text-xs text-slate-500 leading-relaxed max-w-[200px]">
                왼쪽 영역 목록이나 평면도의 다각형 영역을 클릭하면 센서를 배치하고 관리할 수 있습니다.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Name Input Modal */}
      <AnimatePresence>
        {showNameInput && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[150] flex items-center justify-center bg-black/70 backdrop-blur-sm"
            onClick={() => {
              setShowNameInput(false);
              setPendingZonePoints(null);
              setNewZoneName('');
            }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-[#0a0f1a] border border-cyan-500/40 rounded-2xl p-6 w-84 shadow-2xl shadow-cyan-950/50"
            >
              <div className="flex items-center gap-2 mb-3">
                <PentagonIcon className="w-5 h-5 text-cyan-400" />
                <h3 className="text-lg font-bold text-white">새 영역 이름 입력</h3>
              </div>

              <p className="text-xs text-slate-400 mb-4">
                생성한 다각형 영역({pendingZonePoints?.length || 0}개 꼭짓점)의 이름을 지정해 주세요.
              </p>

              <input
                type="text"
                value={newZoneName}
                onChange={(e) => setNewZoneName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddZoneConfirm()}
                placeholder="예: 거실, 안방, 주방, 발코니..."
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/20 text-white placeholder-slate-500 focus:border-cyan-400 focus:outline-none focus:ring-1 focus:ring-cyan-400/40 transition-all font-semibold"
                autoFocus
              />

              <div className="flex gap-3 mt-5">
                <button
                  onClick={() => {
                    setShowNameInput(false);
                    setPendingZonePoints(null);
                    setNewZoneName('');
                  }}
                  className="flex-1 px-4 py-2.5 rounded-xl bg-white/5 border border-white/20 text-slate-300 text-sm font-semibold hover:bg-white/10 transition-all"
                >
                  취소
                </button>
                <button
                  onClick={handleAddZoneConfirm}
                  disabled={!newZoneName.trim()}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-cyan-500 text-slate-950 text-sm font-bold hover:bg-cyan-400 transition-all shadow-lg shadow-cyan-500/20 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <CheckIcon className="w-4 h-4" />
                  생성 완료
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
